import { currentOfflinePrivacyResetEpoch } from './offline-privacy-reset.ts';

const STORAGE_PREFIX = 'blastoclean:trial-banner:v1:';
const dismissedInMemory = new Map<string, string>();
const listeners = new Set<() => void>();

/**
 * Durable dismissal for one user, one company, and one trial deadline.
 * localStorage survives a full browser restart. The stored value is the
 * current sign-in epoch, so sign-out invalidates it even when the key is
 * still there. A different person, company, or deadline is a different key.
 */
export function trialBannerScope(userId: string, companyId: string, trialEndsAt: string | null): string {
  return STORAGE_PREFIX + JSON.stringify([userId, companyId, trialEndsAt]);
}

export function trialBannerDismissed(scope: string): boolean {
  // Read the shared logout epoch even when a storage/BroadcastChannel event
  // was missed. A tab returning after sign-out starts with the completed
  // journal as its baseline, so it must also validate its own storage.
  const epoch = currentOfflinePrivacyResetEpoch();
  if (dismissedInMemory.get(scope) === epoch) return true;
  try {
    return window.localStorage.getItem(scope) === epoch;
  } catch {
    // Restricted browsers still allow dismissal for this document's lifetime.
    return false;
  }
}

export function dismissTrialBanner(scope: string): void {
  const epoch = currentOfflinePrivacyResetEpoch();
  dismissedInMemory.set(scope, epoch);
  try {
    window.localStorage.setItem(scope, epoch);
  } catch {
    // Memory keeps navigation and component remounts working without storage.
  }
  listeners.forEach((listener) => listener());
}

function removePrefixed(storage: Storage): void {
  for (let index = storage.length - 1; index >= 0; index -= 1) {
    const key = storage.key(index);
    if (key?.startsWith(STORAGE_PREFIX)) storage.removeItem(key);
  }
}

/** Sign-out (including expiry and sign-out in another tab) removes the dismissal. */
export function clearTrialBannerDismissals(): void {
  dismissedInMemory.clear();
  // localStorage is the durable copy. sessionStorage is the previous
  // session-only key, cleared so a leftover tab value cannot linger.
  // Each getter sits inside the try: a denied store must not throw out of sign-out.
  for (const read of [() => window.localStorage, () => window.sessionStorage]) {
    try {
      removePrefixed(read());
    } catch {
      // A storage failure must never prevent sign-out.
    }
  }
  listeners.forEach((listener) => listener());
}

export function subscribeTrialBannerDismissals(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
